import { createApi } from '@reduxjs/toolkit/query/react';

import {
    baseQuery,
    SortDir,
} from 'src/services/redux/api';
import { PaginationMeta } from '../video';
import { VIDEO_TAG_TYPE } from '../video/api';
import {
    AddCollectionItem,
    Collection,
    CollectionItem,
    CollectionItemSlim,
    CollectionItemsRequest,
    CollectionListRequest,
    CreateCollection,
    DeleteCollectionItem,
    UpdateCollection,
} from './types';

export const COLLECTION_TAG_TYPE = 'Collection' as string;
export const collectionListTag = { type: COLLECTION_TAG_TYPE, id: 'CollectionList' };

export const COLLECTION_DETAILS_TAG_TYPE = 'CollectionDetails' as string;
export const COLLECTION_ITEMS_TAG_TYPE = 'CollectionItems' as string;

export const collectionApi = createApi({
    baseQuery,
    reducerPath: 'collectionApi',
    tagTypes: [COLLECTION_TAG_TYPE, COLLECTION_DETAILS_TAG_TYPE, COLLECTION_ITEMS_TAG_TYPE],
    endpoints: (builder) => ({
        addCollectionItem: builder.mutation<void, AddCollectionItem>({
            invalidatesTags: (res, err, { collectionId }) => [
                { type: COLLECTION_DETAILS_TAG_TYPE, id: collectionId },
                { type: COLLECTION_ITEMS_TAG_TYPE, id: collectionId },
            ],
            query: ({ collectionId, videoIds }) => ({
                body: { videoIds },
                method: 'POST',
                url: `collection/${collectionId}/items`,
            }),
        }),
        createCollection: builder.mutation<{ data: number }, CreateCollection>({
            invalidatesTags: [collectionListTag],
            query: (body) => ({
                body,
                method: 'POST',
                url: 'collection',
            }),
        }),
        deleteCollection: builder.mutation<void, { id: number }>({
            invalidatesTags: (res, err, { id }) => [
                collectionListTag,
                { type: COLLECTION_DETAILS_TAG_TYPE, id },
                { type: COLLECTION_ITEMS_TAG_TYPE, id },
            ],
            query: ({ id }) => ({
                method: 'DELETE',
                url: `collection/${id}`,
            }),
        }),
        deleteCollectionItem: builder.mutation<void, DeleteCollectionItem>({
            invalidatesTags: (res, err, { collectionId }) => [
                { type: COLLECTION_DETAILS_TAG_TYPE, id: collectionId },
                { type: COLLECTION_ITEMS_TAG_TYPE, id: collectionId },
            ],
            query: ({ collectionId, videoId }) => ({
                method: 'DELETE',
                url: `collection/${collectionId}/items/${videoId}`,
            }),
        }),
        getCollectionItems: builder.query<
            { items: CollectionItem[]; meta: PaginationMeta },
            CollectionItemsRequest
        >({
            transformResponse: (res: { data: { items: CollectionItem[]; meta: PaginationMeta } }) => res?.data,
            providesTags: (res, err, { id }) => [
                { type: COLLECTION_ITEMS_TAG_TYPE, id },
                ...(res?.items.map((item) => ({
                    type: COLLECTION_TAG_TYPE,
                    id: item.id,
                })) || []),
                ...(res?.items.map((item) => ({
                    type: VIDEO_TAG_TYPE,
                    id: item.video.id,
                })) || []),
            ],
            query: ({ id, page, limit }) => ({
                method: 'GET',
                url: `collection/${id}/items?page=${page}&limit=${limit}`,
            }),
        }),
        getAllCollectionItems: builder.query<CollectionItemSlim[], { id: number }>({
            transformResponse: (res: { data: CollectionItemSlim[] }) => res?.data,
            providesTags: (res, err, { id }) => [{ type: COLLECTION_ITEMS_TAG_TYPE, id }],
            query: ({ id }) => ({
                method: 'GET',
                url: `collection/${id}/items/all`,
            }),
        }),
        getCollectionItemsCount: builder.query<number, { id: number }>({
            transformResponse: (res: { data: number }) => res?.data,
            query: ({ id }) => ({
                method: 'GET',
                url: `collection/${id}/items/count`,
            }),
        }),
        getCollections: builder.query<
            { items: Collection[]; meta: PaginationMeta },
            CollectionListRequest
        >({
            providesTags: (res) =>
                res
                    ? [
                        collectionListTag,
                        ...res.items.map(({ id }) => ({
                            type: COLLECTION_TAG_TYPE,
                            id,
                        })),
                    ]
                    : [collectionListTag],
            transformResponse: (res: {
                data: { items: Collection[]; meta: PaginationMeta };
            }) => res?.data,
            query: ({ page = 1, limit = 10, firmId, search, sortBy, sortDir }) => {
                let url = `collection?page=${page}&limit=${limit}`;
                if (firmId) {
                    url += `&firmId=${firmId}`;
                }
                if (search) {
                    url += `&search=${encodeURIComponent(search)}`;
                }
                if (sortBy) {
                    url += `&sortBy=${sortBy}&sortDir=${sortDir ?? SortDir.ASC}`;
                }
                return { method: 'GET', url };
            },
        }),
        getSingleCollection: builder.query<Collection, { id: number }>({
            providesTags: (res, err, { id }) => [
                { type: COLLECTION_DETAILS_TAG_TYPE, id },
            ],
            transformResponse: (res: { data: Collection }) => res?.data,
            query: ({ id }) => ({
                method: 'GET',
                url: `collection/${id}`,
            }),
        }),
        updateCollection: builder.mutation<
            void,
            { id: number; body: UpdateCollection }>({
                invalidatesTags: (res, err, { id }) => [
                    collectionListTag,
                    { type: COLLECTION_DETAILS_TAG_TYPE, id },
                ],
                query: ({ id, body }) => ({
                    body,
                    method: 'PUT',
                    url: `collection/${id}`,
                }),
            }),
        addCollection: builder.mutation<{ data: number }, { body: CreateCollection }>({
            invalidatesTags: (res, err) => [
                collectionListTag,
                { type: COLLECTION_DETAILS_TAG_TYPE },
            ],
            query: ({ body }) => ({
                body,
                method: 'POST',
                url: `collection`,
            })
        }),
        uploadCollectionThumbnail: builder.mutation<void, { id: number; thumbnail: FormData }>({
            invalidatesTags: (res, err, { id }) => [
                collectionListTag,
                { type: COLLECTION_DETAILS_TAG_TYPE, id },
            ],
            query: ({ id, thumbnail }) => ({
                body: thumbnail,
                method: 'POST',
                url: `collection/${id}/upload/thumbnail`,
            }),
        }),
        reorderCollectionItems: builder.mutation<void, { id: number; itemIds: number[] }>({
            invalidatesTags: (res, err, { id }) => [
                { type: COLLECTION_DETAILS_TAG_TYPE, id },
                { type: COLLECTION_ITEMS_TAG_TYPE, id },
            ],
            query: ({ id, itemIds }) => ({
                body: { itemIds },
                method: 'PUT',
                url: `collection/${id}/items/reorder`,
            }),
        }),
        setCollectionItemAltDescription: builder.mutation<void, { collectionId: number; videoId: number; altDescription: string | null }>({
            invalidatesTags: (res, err, { collectionId }) => [
                { type: COLLECTION_ITEMS_TAG_TYPE, id: collectionId },
            ],
            query: ({ collectionId, videoId, altDescription }) => ({
                body: { altDescription },
                method: 'POST',
                url: `collection/${collectionId}/items/${videoId}`,
            }),
        }),
        reorderFirmCollections: builder.mutation<void, { collectionIds: number[]; firmId?: number }>({
            invalidatesTags: [collectionListTag],
            query: ({ collectionIds, firmId }) => ({
                body: { collectionIds, ...(firmId ? { firmId } : {}) },
                method: 'PUT',
                url: 'collection/reorder',
            }),
        }),
    }),
});


export const {
    useGetAllCollectionItemsQuery,
    useLazyGetAllCollectionItemsQuery,
    useAddCollectionItemMutation,
    useCreateCollectionMutation,
    useDeleteCollectionItemMutation,
    useDeleteCollectionMutation,
    useGetCollectionItemsQuery,
    useGetCollectionItemsCountQuery,
    useGetCollectionsQuery,
    useLazyGetCollectionsQuery,
    useGetSingleCollectionQuery,
    useLazyGetSingleCollectionQuery,
    useUpdateCollectionMutation,
    useAddCollectionMutation,
    useUploadCollectionThumbnailMutation,
    useReorderCollectionItemsMutation,
    useReorderFirmCollectionsMutation,
    useSetCollectionItemAltDescriptionMutation,
} = collectionApi;